import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Check, X } from "lucide-react";
import Swal from "sweetalert2";

const ServicesIncludedManage = () => {
  // =====================================================
  // STATE
  // =====================================================

  const [services, setServices] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  const [perPage] = useState(10);

  // Currently editing row
  const [editingId, setEditingId] = useState(null);

  // Edit fields
  const [editServiceName, setEditServiceName] = useState("");
  const [editServiceIcon, setEditServiceIcon] = useState(null);

  // Image preview
  const [editImagePreview, setEditImagePreview] = useState("");

  // Loading states
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // =====================================================
  // IMAGE BASE URL
  // =====================================================

  const IMAGE_BASE_URL =
    process.env.REACT_APP_BASE_URL ||
    "";

  // =====================================================
  // FETCH SERVICES
  // =====================================================

  useEffect(() => {
    fetchServicesData();
  }, []);

  const fetchServicesData = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        "/adminHolidays/get-holidays-services-included-data"
      );

      console.log("SERVICES DATA =>", res?.data);

      if (res?.data?.success) {
        setServices(res?.data?.data || []);
      } else {
        setServices(res?.data?.data || []);

        if (res?.data?.message) {
          toast.error(res.data.message);
        }
      }
    } catch (error) {
      console.error("Fetch Services Error =>", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to load Services Included data"
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // IMAGE URL
  // =====================================================

  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://") ||
      image.startsWith("blob:")
    ) {
      return image;
    }

    return `${IMAGE_BASE_URL}/uploads/holidays_packages/${image}`;
  };

  // =====================================================
  // DELETE SERVICE
  // =====================================================

  const handleDelete = async (id) => {
    if (!id) {
      toast.error("Invalid Service ID");
      return;
    }

    // Prevent delete when another row is editing
    if (editingId !== null && editingId !== id) {
      toast.error(
        "Please update or cancel the current editing row first"
      );
      return;
    }

    const confirm = await Swal.fire({
      title: "Delete Service?",
      text: "Do you want to delete this Services Included?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    try {
      setActionLoading(true);

      const res = await api.delete(
        `/adminHolidays/holidays-services-included-delete/${id}`
      );

      console.log("DELETE SERVICE RESPONSE =>", res?.data);

      if (res?.data?.success) {
        await Swal.fire({
          title: "Deleted!",
          text:
            res?.data?.message ||
            "Services Included deleted successfully",
          icon: "success",
          confirmButtonText: "OK",
        });

        setEditingId(null);
        setEditServiceName("");
        setEditServiceIcon(null);
        setEditImagePreview("");

        await fetchServicesData();

        // If current page becomes empty after delete
        const remainingItems = services.length - 1;
        const newTotalPages = Math.ceil(
          remainingItems / perPage
        );

        if (
          newTotalPages > 0 &&
          currentPage > newTotalPages
        ) {
          setCurrentPage(newTotalPages);
        }
      } else {
        toast.error(
          res?.data?.message ||
            "Failed to delete Services Included"
        );
      }
    } catch (error) {
      console.error("Delete Service Error =>", error);

      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete Services Included"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // =====================================================
  // STATUS CHANGE
  // =====================================================

  const handleStatusChange = async (id, is_active) => {
    if (!id) {
      toast.error("Invalid Service ID");
      return;
    }

    // Prevent status change while another row is editing
    if (editingId !== null && editingId !== id) {
      toast.error(
        "Please update or cancel the current editing row first"
      );
      return;
    }

    try {
      setActionLoading(true);

      const res = await api.put(
        "/adminHolidays/holidays-services-included-status",
        {
          id,
          is_active: Number(is_active),
        }
      );

      console.log("STATUS RESPONSE =>", res?.data);

      if (res?.data?.success) {
        toast.success(
          res?.data?.message ||
            "Status updated successfully"
        );

        await fetchServicesData();
      } else {
        toast.error(
          res?.data?.message ||
            "Failed to update status"
        );
      }
    } catch (error) {
      console.error("Status Update Error =>", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to update status"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // =====================================================
  // START EDIT
  // =====================================================

  const handleEdit = (service) => {
    if (!service?.id) {
      toast.error("Invalid Service ID");
      return;
    }

    // Another row already editing
    if (
      editingId !== null &&
      editingId !== service.id
    ) {
      toast.error(
        "Please update or cancel the current editing row first"
      );
      return;
    }

    setEditingId(service.id);

    setEditServiceName(
      service.service_name ??
        service.name ??
        ""
    );

    setEditServiceIcon(null);

    const currentImage =
      service.service_icon ||
      service.icon_file ||
      service.icon ||
      "";

    setEditImagePreview(
      currentImage ? getImageUrl(currentImage) : ""
    );
  };

  // =====================================================
  // CANCEL EDIT
  // =====================================================

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditServiceName("");
    setEditServiceIcon(null);
    setEditImagePreview("");
  };

  // =====================================================
  // ICON CHANGE
  // =====================================================

  const handleIconChange = (e) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      return;
    }

    // Allowed image types
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/svg+xml",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error(
        "Only JPG, JPEG, PNG, WEBP and SVG files are allowed"
      );

      e.target.value = "";
      return;
    }

    // 2 MB
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2 MB");

      e.target.value = "";
      return;
    }

    setEditServiceIcon(file);

    setEditImagePreview(
      URL.createObjectURL(file)
    );
  };

  // =====================================================
  // UPDATE SERVICE
  // =====================================================

  const handleUpdate = async (id) => {
    // =====================================================
    // VALIDATION
    // =====================================================

    if (
      String(editServiceName).trim() === ""
    ) {
      toast.error("Services Included Name is required");
      return;
    }

    // =====================================================
    // CONFIRMATION
    // =====================================================

    const confirm = await Swal.fire({
      title: "Update Service?",
      text: "Do you want to update this Services Included?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    try {
      setActionLoading(true);

      // =====================================================
      // FORM DATA
      // =====================================================

      const formData = new FormData();

      formData.append(
        "id",
        id
      );

      formData.append(
        "service_name",
        String(editServiceName).trim()
      );

      // Only send new image if selected
      if (editServiceIcon) {
        formData.append(
          "icon_file",
          editServiceIcon
        );
      }

      console.log(
        "UPDATE SERVICE =>",
        {
          id,
          service_name: editServiceName,
          newIcon: editServiceIcon,
        }
      );

      const res = await api.put(
        "/adminHolidays/holidays-services-included-update",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log(
        "UPDATE SERVICE RESPONSE =>",
        res?.data
      );

      if (res?.data?.success) {
        await Swal.fire({
          title: "Updated!",
          text:
            res?.data?.message ||
            "Services Included updated successfully",
          icon: "success",
          confirmButtonText: "OK",
        });

        handleCancelEdit();

        await fetchServicesData();
      } else {
        toast.error(
          res?.data?.message ||
            "Unable to update Services Included"
        );
      }
    } catch (error) {
      console.error(
        "Update Service Error =>",
        error
      );

      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Server Error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // =====================================================
  // PAGINATION
  // =====================================================

  const indexOfLast =
    currentPage * perPage;

  const indexOfFirst =
    indexOfLast - perPage;

  const currentItems =
    services.slice(
      indexOfFirst,
      indexOfLast
    );

  const totalPages =
    Math.ceil(
      services.length / perPage
    );

  // =====================================================
  // PAGE CHANGE
  // =====================================================

  const handlePageChange = (page) => {
    if (
      page < 1 ||
      page > totalPages
    ) {
      return;
    }

    // Cancel edit when page changes
    handleCancelEdit();

    setCurrentPage(page);
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">

      {/* =================================================
          LOADING OVERLAY
      ================================================== */}

      

      {/* =================================================
          HEADER
      ================================================== */}

      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">
        <div className="card-body d-flex justify-content-between align-items-center">

          <h4 className="fw-bold mb-0">
            Manage Services Included
          </h4>

          <Link
            to="/holidays/services-included/add-services-included"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} />
            Add Services Included
          </Link>

        </div>
      </div>

      {/* =================================================
          TABLE CARD
      ================================================== */}

      <div className="card shadow-sm border-0">

        <div className="table-responsive">

          <table className="table table-hover table-sm align-middle mb-0">

            {/* =================================================
                TABLE HEADER
            ================================================== */}

            <thead className="bg-light">

              <tr>

                <th>
                  Sl
                </th>

                <th>
                  Services Image
                </th>

                <th>
                  Services Included Name
                </th>

                <th>
                  Status
                </th>

                <th>
                  Action
                </th>

              </tr>

            </thead>

            {/* =================================================
                TABLE BODY
            ================================================== */}

            <tbody>

              {currentItems.length === 0 ? (

                <tr>

                  <td
                    colSpan="5"
                    className="text-center py-5 text-muted"
                  >
                    {loading
                      ? "Loading..."
                      : "No Services Included Found"}
                  </td>

                </tr>

              ) : (

                currentItems.map(
                  (service, index) => {

                    const isEditing =
                      editingId === service.id;

                    const isOtherRowEditing =
                      editingId !== null &&
                      editingId !== service.id;

                    const serviceImage =
                      service.service_icon ||
                      service.icon_file ||
                      service.icon ||
                      "";

                    const serviceName =
                      service.service_name ||
                      service.name ||
                      "";

                    return (

                      <tr
                        key={service.id}
                        className={
                          isEditing
                            ? "table-primary"
                            : ""
                        }
                      >

                        {/* =================================
                            SL
                        ================================== */}

                        <td>
                          {indexOfFirst +
                            index +
                            1}
                        </td>

                        {/* =================================
                            SERVICE IMAGE
                        ================================== */}

                        <td>

                          {isEditing ? (

                            <div
                              className="d-flex align-items-center gap-3"
                            >

                              <div>
                                {editImagePreview ? (
                                  <img
                                    src={
                                      editImagePreview
                                    }
                                    alt="Service"
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit:
                                        "contain",
                                      border:
                                        "1px solid #ddd",
                                      borderRadius:
                                        "6px",
                                      padding: "4px",
                                      background:
                                        "#fff",
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="text-muted small"
                                  >
                                    No Image
                                  </div>
                                )}
                              </div>

                              <div>
                                <input
                                  type="file"
                                  className="form-control form-control-sm"
                                  accept=".jpg,.jpeg,.png,.webp,.svg"
                                  onChange={
                                    handleIconChange
                                  }
                                />

                                <small className="text-muted">
                                  Max 2 MB
                                </small>
                              </div>

                            </div>

                          ) : (

                            serviceImage ? (

                              <img
                                src={getImageUrl(
                                  serviceImage
                                )}
                                alt={serviceName}
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit:
                                    "contain",
                                  border:
                                    "1px solid #ddd",
                                  borderRadius:
                                    "6px",
                                  padding: "4px",
                                  background:
                                    "#fff",
                                }}
                              />

                            ) : (

                              <span className="text-muted">
                                No Image
                              </span>

                            )

                          )}

                        </td>

                        {/* =================================
                            SERVICE NAME
                        ================================== */}

                        <td>

                          {isEditing ? (

                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={
                                editServiceName
                              }
                              onChange={(e) =>
                                setEditServiceName(
                                  e.target.value
                                )
                              }
                              placeholder="Services Included Name"
                            />

                          ) : (

                            <span className="fw-semibold">
                              {serviceName ||
                                "-"}
                            </span>

                          )}

                        </td>

                        {/* =================================
                            STATUS
                        ================================== */}

                        <td>

                          <select
                            className={`form-select form-select-sm ${
                              Number(
                                service.is_active
                              ) === 1
                                ? "text-success"
                                : "text-danger"
                            }`}
                            value={
                              service.is_active ??
                              1
                            }
                            disabled={
                              isEditing ||
                              isOtherRowEditing ||
                              actionLoading
                            }
                            onChange={(e) =>
                              handleStatusChange(
                                service.id,
                                e.target.value
                              )
                            }
                            style={{
                              maxWidth: "120px",
                            }}
                          >

                            <option value="1">
                              Active
                            </option>

                            <option value="0">
                              Inactive
                            </option>

                          </select>

                        </td>

                        {/* =================================
                            ACTION
                        ================================== */}

                        <td>

                          {isEditing ? (

                            <>

                              {/* UPDATE */}

                              <button
                                type="button"
                                className="btn btn-sm btn-success me-2"
                                disabled={
                                  actionLoading
                                }
                                onClick={() =>
                                  handleUpdate(
                                    service.id
                                  )
                                }
                              >

                                {actionLoading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-1"
                                      role="status"
                                    />
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <Check
                                      size={15}
                                    />
                                    &nbsp;
                                    Update
                                  </>
                                )}

                              </button>

                              {/* CANCEL */}

                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                disabled={
                                  actionLoading
                                }
                                onClick={
                                  handleCancelEdit
                                }
                              >
                                <X size={15} />
                                &nbsp;
                                Cancel
                              </button>

                            </>

                          ) : (

                            <>

                              {/* EDIT */}

                              <button
                                type="button"
                                className="btn btn-sm btn-primary me-2"
                                disabled={
                                  isOtherRowEditing ||
                                  actionLoading
                                }
                                onClick={() =>
                                  handleEdit(
                                    service
                                  )
                                }
                                title={
                                  isOtherRowEditing
                                    ? "Please update or cancel current editing row first"
                                    : "Edit Service"
                                }
                              >

                                <Pencil
                                  size={14}
                                />

                              </button>

                              {/* DELETE */}

                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                disabled={
                                  isOtherRowEditing ||
                                  actionLoading
                                }
                                onClick={() =>
                                  handleDelete(
                                    service.id
                                  )
                                }
                                title={
                                  isOtherRowEditing
                                    ? "Please update or cancel current editing row first"
                                    : "Delete Service"
                                }
                              >

                                <Trash2
                                  size={14}
                                />

                              </button>

                            </>

                          )}

                        </td>

                      </tr>

                    );
                  }
                )

              )}

            </tbody>

          </table>

        </div>

        {/* =================================================
            PAGINATION
        ================================================== */}

        {totalPages > 0 && (

          <div className="d-flex justify-content-center mt-3 mb-3">

            <ul className="pagination mb-0">

              {/* PREVIOUS */}

              <li
                className={`page-item ${
                  currentPage === 1
                    ? "disabled"
                    : ""
                }`}
              >

                <button
                  type="button"
                  className="page-link"
                  disabled={
                    currentPage === 1
                  }
                  onClick={() =>
                    handlePageChange(
                      currentPage - 1
                    )
                  }
                >
                  Prev
                </button>

              </li>

              {/* PAGE NUMBERS */}

              {Array.from(
                {
                  length: totalPages,
                },
                (_, i) => (

                  <li
                    key={i}
                    className={`page-item ${
                      currentPage === i + 1
                        ? "active"
                        : ""
                    }`}
                  >

                    <button
                      type="button"
                      className="page-link"
                      onClick={() =>
                        handlePageChange(
                          i + 1
                        )
                      }
                    >
                      {i + 1}
                    </button>

                  </li>

                )
              )}

              {/* NEXT */}

              <li
                className={`page-item ${
                  currentPage === totalPages
                    ? "disabled"
                    : ""
                }`}
              >

                <button
                  type="button"
                  className="page-link"
                  disabled={
                    currentPage ===
                    totalPages
                  }
                  onClick={() =>
                    handlePageChange(
                      currentPage + 1
                    )
                  }
                >
                  Next
                </button>

              </li>

            </ul>

          </div>

        )}

      </div>

    </div>
  );
};

export default ServicesIncludedManage;